import { IntlProvider } from 'react-intl';
import * as Sentry from '@sentry/react';
import { BrowserRouter } from 'react-router-dom';
import { Amplify } from 'aws-amplify';

// Hooks
import useI18n from "./hooks/useI18n";
import useEnv from "./hooks/useEnv";

// Translationfiles.dsaa
import '@formatjs/intl-pluralrules/locale-data/fi'; // locale data for fi
import '@formatjs/intl-pluralrules/locale-data/en'; // locale data for en
import '@formatjs/intl-pluralrules/locale-data/se'; // locale data for se
import '@formatjs/intl-pluralrules/locale-data/no'; // locale data for no

import locales_fi from './locales/fi.json';
import locales_en from './locales/en.json';
import locales_se from './locales/se.json';
import locales_no from './locales/no.json';
import MessageContextProvider from "./contexts/MessageContext";
import UserAccountContextProvider from "./contexts/UserAccountContext";
import AuthContextProvider from "./contexts/AuthContext";
import App from "./App";
import JobContextProvider from "./contexts/JobContext";

// translations
const messages = {
    fi: locales_fi,
    en: locales_en,
    se: locales_se,
    no: locales_no
};

Amplify.configure({
    Auth: {
        Cognito: {
            userPoolClientId: import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID,
            userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID
        }
    }
});

const Main = () => {
    const { setupLanguage, setupCountry, getLocaleBCP47 } = useI18n();
    const { getEnv } = useEnv();

    // Language is defined as a global variable, and it's exist in global context of application.
    // Every time the main component of the application is rendered it must be empty.
    const language = setupLanguage();
    setupCountry();

    // Keep the html lang attribute in sync so assistive technologies use the correct pronunciation.
    document.documentElement.lang = getLocaleBCP47().replace('_', '-');

    // Redirect?
    const redirectUrl = getEnv('REDIRECT_URL');
    if (redirectUrl !== undefined && redirectUrl !== '') {
        window.location = window.location.href.replace(window.location.origin, redirectUrl);
        return null;
    }

    // replace www-prefix with VITE_URL for redirects to work correctly
    const siteUrl = getEnv('URL');
    if (window.location.hostname.startsWith("www") && siteUrl !== undefined) {
        window.location = window.location.href.replace(window.location.origin, siteUrl);
    }

    return (
        <Sentry.ErrorBoundary fallback={"An error has occured"}>
            <BrowserRouter>
                <IntlProvider locale={language} messages={messages[language]}>
                    <AuthContextProvider>
                        <JobContextProvider>
                            <MessageContextProvider>
                                <UserAccountContextProvider>
                                    <App />
                                </UserAccountContextProvider>
                            </MessageContextProvider>
                        </JobContextProvider>
                    </AuthContextProvider>
                </IntlProvider>
            </BrowserRouter>
        </Sentry.ErrorBoundary>
    );
};

export default Main;
